(() => {
  'use strict';
  if (customElements.get('difiney-fit-molding')) return;

  const counted = new Set();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const editorMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class FitSection extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.init());
    }

    init() {
      if (this.controller || !this.isConnected) return false;
      this.controller = new AbortController();
      this.observers = [];
      if (!reducedMotion() && !editorMode() && 'IntersectionObserver' in window) {
        const reveal = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-entering');
            reveal.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        this.querySelectorAll('[data-fit-reveal]').forEach((node) => reveal.observe(node));
        this.observers.push(reveal);
      }
      return true;
    }

    listen(node, name, callback, options = {}) {
      if (node) node.addEventListener(name, callback, { ...options, signal: this.controller.signal });
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.countFrame);
      if (this.controller) this.controller.abort();
      (this.observers || []).forEach((observer) => observer.disconnect());
      this.controller = null;
      this.querySelectorAll('[data-fit-reveal]').forEach((node) => node.classList.remove('is-entering'));
    }

    disconnectedCallback() { this.destroy(); }
  }

  class FitMolding extends FitSection {
    init() {
      if (!super.init()) return;
      this.video = this.querySelector('video');
      this.steps = Array.from(this.querySelectorAll('[data-fit-step]'));
      this.pendingSeek = null;
      this.pendingStep = null;
      this.playAfterSeek = false;
      this.metadataRequested = false;
      this.editorStep = null;
      this.activeStep = null;
      this.chapters = this.steps.map((step) => ({ step, time: this.timestamp(step) }))
        .filter((chapter) => chapter.time !== null)
        .sort((a, b) => a.time - b.time);

      this.steps.forEach((step) => this.listen(step, 'click', () => {
        this.select(step);
        const time = this.timestamp(step);
        if (!this.video || time === null) {
          this.pendingSeek = null;
          this.pendingStep = null;
          this.playAfterSeek = false;
          return;
        }
        this.pendingSeek = time;
        this.pendingStep = step;
        this.playAfterSeek = true;
        if (this.video.readyState >= 1) {
          this.applySeek();
        } else if (!this.metadataRequested) {
          // Further clicks replace the requested chapter without restarting load.
          this.metadataRequested = true;
          this.video.preload = 'metadata';
          this.video.load();
        }
      }));
      this.listen(this.video, 'loadedmetadata', () => {
        this.metadataRequested = false;
        this.applySeek();
        this.syncFromVideo();
      });
      this.listen(this.video, 'canplay', () => this.applySeek());
      this.listen(this.video, 'durationchange', () => {
        this.applySeek();
        this.syncFromVideo();
      });
      ['timeupdate', 'seeking', 'seeked', 'ended', 'emptied'].forEach((eventName) => {
        this.listen(this.video, eventName, () => this.syncFromVideo());
      });
      this.listen(this.video, 'error', () => {
        this.pendingSeek = null;
        this.pendingStep = null;
        this.playAfterSeek = false;
        this.metadataRequested = false;
        this.syncFromVideo();
      });

      this.listen(document, 'shopify:block:select', (event) => {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const block = this.querySelector(`[data-fit-block="${CSS.escape(String(blockId))}"]`);
        const step = block && block.querySelector('[data-fit-step]');
        if (!step || !this.steps.includes(step)) return;
        this.editorStep = step;
        this.select(step);
        block.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
      this.listen(document, 'shopify:block:deselect', (event) => {
        const block = this.editorStep && this.editorStep.closest('[data-fit-block]');
        if (!block || block.dataset.fitBlock !== String(event.detail && event.detail.blockId)) return;
        this.editorStep = null;
        this.syncFromVideo();
      });
      this.syncFromVideo();

      // Autoplay is opt-in, muted, one-time, and disabled for reduced motion/editor.
      // Native controls remain available and user-initiated playback is untouched.
      if (this.video && this.video.dataset.fitAutoplay === 'true' && !reducedMotion() && !editorMode() && 'IntersectionObserver' in window) {
        const autoplay = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          autoplay.disconnect();
          this.video.muted = true;
          this.playVideo();
        }, { threshold: 0.35 });
        autoplay.observe(this.video);
        this.observers.push(autoplay);
      }
    }

    timestamp(step) {
      const raw = String(step.dataset.timestamp || '').trim();
      if (!raw) return null;
      const value = Number(raw);
      return Number.isFinite(value) && value >= 0 ? value : null;
    }

    select(step, progress = 0) {
      const selected = this.steps.includes(step) ? step : this.steps[0];
      const fraction = Number.isFinite(progress) ? clamp(progress, 0, 1) : 0;
      this.activeStep = selected || null;
      this.steps.forEach((item) => {
        const active = item === selected;
        item.setAttribute('aria-pressed', String(active));
        item.style.setProperty('--fit-step-progress', String(active ? fraction : 0));
      });
    }

    syncFromVideo() {
      if (this.editorStep) {
        this.select(this.editorStep);
        return;
      }
      if (this.pendingSeek !== null && this.pendingStep) {
        this.select(this.pendingStep);
        return;
      }
      if (!this.video || !this.chapters.length) {
        this.select(this.activeStep || this.steps[0]);
        return;
      }
      const time = Number.isFinite(this.video.currentTime) ? Math.max(0, this.video.currentTime) : 0;
      let index = -1;
      this.chapters.forEach((chapter, chapterIndex) => {
        if (chapter.time <= time) index = chapterIndex;
      });
      // Before the first configured timestamp, including native replay at 0,
      // the first visible step remains the one and only active chapter.
      if (index < 0) {
        this.select(this.steps[0]);
        return;
      }
      const chapter = this.chapters[index];
      const next = this.chapters.slice(index + 1).find((item) => item.time > chapter.time);
      const end = next ? next.time : this.video.duration;
      const progress = Number.isFinite(end) && end > chapter.time ? (time - chapter.time) / (end - chapter.time) : 0;
      this.select(chapter.step, progress);
    }

    applySeek() {
      if (!this.video || this.pendingSeek === null || this.video.readyState < 1) return;
      const time = Number.isFinite(this.video.duration) ? clamp(this.pendingSeek, 0, Math.max(0, this.video.duration)) : this.pendingSeek;
      try {
        this.video.currentTime = time;
      } catch (_) {
        // Retry through the single canplay/durationchange listeners. Do not add
        // a fresh retry listener for every click or metadata event.
        return;
      }
      this.pendingSeek = null;
      this.pendingStep = null;
      const shouldPlay = this.playAfterSeek;
      this.playAfterSeek = false;
      this.syncFromVideo();
      if (shouldPlay) this.playVideo();
    }

    playVideo() {
      try {
        const playback = this.video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(() => {});
      } catch (_) {
        // If playback is blocked, native video controls remain available.
      }
    }

    destroy() {
      super.destroy();
      if (this.video) this.video.pause();
      this.pendingSeek = null;
      this.pendingStep = null;
      this.playAfterSeek = false;
      this.metadataRequested = false;
      this.editorStep = null;
    }
  }

  class FitComparison extends FitSection {
    init() {
      if (!super.init()) return;
      const comparison = this.querySelector('.dif-fit__comparison');
      const canvas = this.querySelector('[data-fit-canvas]');
      const range = this.querySelector('[data-fit-range]');
      if (comparison && canvas && range) {
        const control = this.querySelector('[data-fit-range-control]');
        const showButtons = Array.from(this.querySelectorAll('[data-fit-show]'));
        const resetButtons = Array.from(this.querySelectorAll('[data-fit-reset]'));
        let currentPosition = 50;
        this.comparisonFrame = null;
        this.cancelComparisonMotion = () => {
          if (this.comparisonFrame !== null) cancelAnimationFrame(this.comparisonFrame);
          this.comparisonFrame = null;
        };
        const update = (value) => {
          const position = clamp(Number(value), 0, 100);
          if (!Number.isFinite(position)) return;
          currentPosition = position;
          const percentage = Math.round(position);
          const mode = position === 100 ? 'before' : position === 0 ? 'after' : 'compare';
          range.value = String(percentage);
          canvas.style.setProperty('--fit-position', `${position}%`);
          canvas.dataset.fitMode = mode;
          showButtons.forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.fitShow === mode));
          });
          range.setAttribute('aria-valuetext', `${percentage}% ${range.dataset.beforeLabel}, ${100 - percentage}% ${range.dataset.afterLabel}`);
        };
        const animateTo = (position) => {
          this.cancelComparisonMotion();
          if (reducedMotion() || currentPosition === position) {
            update(position);
            return;
          }
          const from = currentPosition;
          const started = performance.now();
          const tick = (now) => {
            const progress = reducedMotion() ? 1 : clamp((now - started) / 350, 0, 1);
            update(from + (position - from) * (1 - Math.pow(1 - progress, 3)));
            if (progress < 1) this.comparisonFrame = requestAnimationFrame(tick);
            else {
              update(position);
              this.comparisonFrame = null;
            }
          };
          this.comparisonFrame = requestAnimationFrame(tick);
        };
        const updateFromPointer = (event) => {
          const bounds = canvas.getBoundingClientRect();
          if (bounds.width) update(Math.round((event.clientX - bounds.left) / bounds.width * 100));
        };
        let pointerId = null;
        showButtons.forEach((button) => this.listen(button, 'click', () => {
          if (button.dataset.fitShow === 'before') animateTo(100);
          if (button.dataset.fitShow === 'after') animateTo(0);
        }));
        resetButtons.forEach((button) => this.listen(button, 'click', () => animateTo(50)));
        this.listen(range, 'pointerdown', () => this.cancelComparisonMotion());
        this.listen(range, 'keydown', () => this.cancelComparisonMotion());
        this.listen(range, 'input', () => {
          this.cancelComparisonMotion();
          update(range.value);
        });
        this.listen(canvas, 'pointerdown', (event) => {
          if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
          this.cancelComparisonMotion();
          pointerId = event.pointerId;
          canvas.classList.add('is-interacting');
          comparison.classList.add('is-interacting');
          canvas.setPointerCapture(pointerId);
          updateFromPointer(event);
          range.focus({ preventScroll: true });
        });
        this.listen(canvas, 'pointermove', (event) => {
          if (event.pointerId === pointerId) updateFromPointer(event);
        });
        const endDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          if (canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId);
          pointerId = null;
          canvas.classList.remove('is-interacting');
          comparison.classList.remove('is-interacting');
        };
        this.listen(canvas, 'pointerup', endDrag);
        this.listen(canvas, 'pointercancel', endDrag);
        this.listen(canvas, 'lostpointercapture', () => {
          pointerId = null;
          canvas.classList.remove('is-interacting');
          comparison.classList.remove('is-interacting');
        });
        this.listen(canvas, 'dragstart', (event) => event.preventDefault());
        update(50);
        comparison.classList.add('is-enhanced');
        if (control) control.hidden = false;
      }
      this.prepareCount();
    }

    prepareCount() {
      const proof = this.querySelector('[data-fit-proof]');
      if (!proof || proof.dataset.animate !== 'true' || reducedMotion() || editorMode()) return;
      const value = (proof.getAttribute('aria-label') || '').trim();
      const match = value.match(/^(\d{1,3})(%)$/);
      const key = `${this.dataset.sectionId}:${value}`;
      if (!match || counted.has(key) || !('IntersectionObserver' in window)) return;
      const text = proof.querySelector('span');
      if (!text) return;
      const target = Number(match[1]);
      const signal = this.controller.signal;
      // Keep the server-rendered final value for no-JS and reduced-motion users.
      text.textContent = '0%';
      const startCount = () => {
        if (signal.aborted || !this.isConnected || counted.has(key)) return;
        counted.add(key);
        let started = null;
        const tick = (now) => {
          if (signal.aborted || !this.isConnected) return;
          if (started === null) started = now;
          const progress = reducedMotion() ? 1 : clamp((now - started) / 1200, 0, 1);
          text.textContent = `${Math.round(target * (1 - Math.pow(1 - progress, 3)))}%`;
          if (progress < 1) this.countFrame = requestAnimationFrame(tick);
          else {
            text.textContent = value;
            this.countFrame = null;
          }
        };
        this.countFrame = requestAnimationFrame(tick);
      };
      const observer = new IntersectionObserver((entries) => {
        if (signal.aborted || !this.isConnected) return;
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        observer.disconnect();
        startCount();
      }, { threshold: 0.5 });
      observer.observe(proof);
      this.observers.push(observer);
    }

    destroy() {
      if (this.cancelComparisonMotion) this.cancelComparisonMotion();
      this.querySelectorAll('.is-interacting').forEach((node) => node.classList.remove('is-interacting'));
      const proof = this.querySelector('[data-fit-proof]');
      if (proof) proof.querySelector('span').textContent = proof.getAttribute('aria-label');
      super.destroy();
    }
  }

  customElements.define('difiney-fit-molding', FitMolding);
  customElements.define('difiney-fit-comparison', FitComparison);

  // Custom-element lifecycle covers normal navigation, section insertion/reorder,
  // and editor rerenders. Explicit events also support editor reload timing.
  const sectionsWithin = (node) => {
    if (!(node instanceof Element)) return [];
    return [node, ...node.querySelectorAll('difiney-fit-molding, difiney-fit-comparison')]
      .filter((element) => element instanceof FitSection);
  };
  document.addEventListener('shopify:section:load', (event) => sectionsWithin(event.target).forEach((section) => section.init()));
  document.addEventListener('shopify:section:unload', (event) => sectionsWithin(event.target).forEach((section) => section.destroy()));
})();
